import { useEffect, useMemo, useRef, useState } from "react";

/**
 * Type-to-search for the 538 sponsored occupations.
 *
 * Written rather than taken from multiselect-react-dropdown, which is
 * already in the app and looked like the obvious choice: its source reads
 * `disabled: singleSelect || disable`, so it disables its own search input
 * whenever single select is on. Searchable and single-select cannot both be
 * true in that library, and scrolling 538 options is not an option.
 *
 * Single select: an employer who sponsors two chosen roles would otherwise
 * appear twice with two different counts, and "N employers sponsored this"
 * would stop being true.
 */
export default function RolePicker({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.soc_code === String(value || "")),
    [options, value],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.search.includes(q));
  }, [options, query]);

  useEffect(() => {
    const away = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  // Keep the highlighted option in view when arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (opt) => {
    onChange(opt ? opt.soc_code : "");
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActive((i) => {
        const next = e.key === "ArrowDown" ? i + 1 : i - 1;
        if (next < 0) return matches.length - 1;
        if (next >= matches.length) return 0;
        return next;
      });
    } else if (e.key === "Enter") {
      if (open && matches[active]) {
        e.preventDefault();
        choose(matches[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="role_picker" ref={boxRef}>
      <div className="field">
        <input
          type="text"
          className="input"
          role="combobox"
          aria-expanded={open}
          aria-controls="role_options"
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Sponsored role - type to search"
          value={open ? query : selected ? selected.label : ""}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            if (!open) setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        {selected && !open && (
          <button
            type="button"
            className="clear"
            aria-label="Clear the selected role"
            onClick={() => choose(null)}
          >
            &times;
          </button>
        )}
      </div>

      {open && (
        <ul className="options" id="role_options" role="listbox" ref={listRef}>
          {matches.length === 0 && <li className="empty">No matching role</li>}
          {matches.slice(0, 120).map((o, i) => (
            <li
              key={o.soc_code}
              role="option"
              aria-selected={o.soc_code === String(value || "")}
              className={i === active ? "active" : undefined}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(o);
              }}
            >
              <span className="name">{o.soc_name}</span>
              <span className="count">
                {Number(o.employers).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
